import { toInteger } from 'lodash';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import type { ILayerProps, ISize, IWatermarkLayerProps } from '@/version-types';
import { computeFullScreenWatermarkSize } from '../utils/watermark';
import { renderLayerContentToCanvas } from './render-to-canvas';

export const renderLayerToStage = async (
    layer: ILayerProps,
    stageSize: ISize,
): Promise<HTMLCanvasElement | undefined> => {
    const { url, originUrl } = layer.data as IWatermarkLayerProps;

    // 移动端过来的脏数据会导致下载异常
    if (!url && !originUrl) return;

    if (url) {
        await base64Kit.getRemoteUrlAsync(url);
    }

    const { width, height, left, top, opacity, rotate } = layer;
    const { isFullScreen, size } = layer.data as IWatermarkLayerProps;
    const { width: stageWidth, height: stageHeight } = stageSize;
    const resultWidth = Math.max(1, toInteger(width));
    const resultHeight = Math.max(1, toInteger(height));

    if (isFullScreen) {
        const sourceUrl = url || originUrl;
        const result = await getImgInfoAsync(sourceUrl);
        const watermarkSize = computeFullScreenWatermarkSize(
            size,
            {
                width: result.width,
                height: result.height,
            },
            {
                width: stageWidth,
                height: stageHeight,
            },
        );
        const baseImage = createCanvas(
            Math.max(1, toInteger(watermarkSize.width)),
            Math.max(1, toInteger(watermarkSize.height)),
            result.img,
        ).canvas;
        const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
        const pattern = destCtx.createPattern(baseImage, 'repeat');
        destCtx.globalAlpha = opacity;
        if (pattern) {
            destCtx.fillStyle = pattern;
            destCtx.fillRect(0, 0, destCanvas.width, destCanvas.height);
        }
        return destCanvas;
    }

    const layerImage = await renderLayerContentToCanvas(layer);
    const centerPoint = {
        x: resultWidth / 2 + left,
        y: resultHeight / 2 + top,
    };
    const drawOffsetPoint = {
        x: -resultWidth / 2,
        y: -resultHeight / 2,
    };
    const { canvas: stageCanvas, ctx: stageCtx } = createCanvas(stageWidth, stageHeight);

    stageCtx.translate(centerPoint.x, centerPoint.y);
    stageCtx.rotate((Math.PI / 180) * rotate);
    stageCtx.globalAlpha = opacity;
    stageCtx.drawImage(
        layerImage,
        0,
        0,
        resultWidth,
        resultHeight,
        drawOffsetPoint.x,
        drawOffsetPoint.y,
        resultWidth,
        resultHeight,
    );

    return stageCanvas;
};
